import React, { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "../../components/ui/button";
import { useNotification } from "../../contexts/NotificationContext";
import { usePricing } from "../../contexts/PricingContext";
import { ANY_ZONE, Route, flattenIssues, routeSchema } from "../../lib/pricing";
import { saveRoute } from "../pricing/repository";
import { ANY_OPTION, decodeEndpoint, encodeEndpoint, endpointGroups } from "../pricing/endpoints";
import { FormSection, SelectField, SwitchField } from "../components/pricing/fields";
import TierEditor, { TierDraft } from "../components/pricing/TierEditor";
import { PRICING_PATH } from "./ZoneForm";

interface Props {
  id?: string;
  route?: Route;
}

const defaultTiers = (maxPassengers: number): TierDraft[] => [
  { minPax: 1, maxPax: Math.min(4, maxPassengers), price: "" },
  ...(maxPassengers > 4 ? [{ minPax: 5, maxPax: maxPassengers, price: "" } as TierDraft] : []),
];

export default function RouteForm({ id, route }: Props) {
  const { zones, places, zoneNames, data } = usePricing();
  const [origin, setOrigin] = useState(
    route ? encodeEndpoint(route.originZoneId, route.originPlaceId) : ANY_ZONE
  );
  const [destination, setDestination] = useState(
    route ? encodeEndpoint(route.destinationZoneId, route.destinationPlaceId) : ""
  );
  const [bidirectional, setBidirectional] = useState(route?.bidirectional ?? true);
  const [active, setActive] = useState(route?.active ?? true);
  const [tiers, setTiers] = useState<TierDraft[]>(
    route?.tiers?.length ? route.tiers : defaultTiers(data.config.maxPassengers)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const groups = endpointGroups(zones, places, zoneNames);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const from = decodeEndpoint(origin, data.places);
    const to = decodeEndpoint(destination, data.places);
    const parsed = routeSchema.safeParse({
      originZoneId: from.zoneId,
      originPlaceId: from.placeId,
      destinationZoneId: to.zoneId,
      destinationPlaceId: to.placeId,
      bidirectional,
      active,
      tiers: tiers.map((t) => ({
        minPax: t.minPax === "" ? NaN : t.minPax,
        maxPax: t.maxPax === "" ? NaN : t.maxPax,
        price: t.price === "" ? NaN : t.price,
      })),
    });
    if (!parsed.success) {
      setErrors(flattenIssues(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await saveRoute(parsed.data, id);
      showNotification("success", id ? "Tarifa atualizada." : "Tarifa criada.");
      navigate(`${PRICING_PATH}?tab=routes`);
    } catch {
      showNotification("error", "Não foi possível salvar a tarifa.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <FormSection title="Trajeto">
        <SelectField
          label="Origem"
          value={origin}
          onChange={setOrigin}
          options={[ANY_OPTION]}
          groups={groups}
          error={errors.originZoneId}
          hint='Uma região inteira, um local específico ou "Qualquer origem" (o preço depende só do destino).'
        />
        <SelectField
          label="Destino *"
          value={destination}
          onChange={setDestination}
          groups={groups}
          placeholder="Selecione a região ou o local"
          error={errors.destinationZoneId}
          hint="Tarifas entre locais têm prioridade sobre as tarifas da região. Ex.: Canasvieiras → Ingleses."
        />
        <SwitchField
          label="Vale nos dois sentidos"
          description="Se ligado, a tarifa também se aplica do destino para a origem."
          checked={bidirectional}
          onChange={setBidirectional}
        />
      </FormSection>

      <FormSection title="Faixas de passageiros">
        <TierEditor
          tiers={tiers}
          onChange={setTiers}
          errors={errors}
          maxPassengers={data.config.maxPassengers}
        />
      </FormSection>

      <FormSection title="Status">
        <SwitchField label="Tarifa ativa" checked={active} onChange={setActive} />
      </FormSection>

      <Button type="submit" className="w-full" disabled={saving || zones.length === 0}>
        {id ? "Atualizar" : "Adicionar"}
      </Button>
    </form>
  );
}
